import { ref, reactive, computed, watch, nextTick } from 'vue';
import { Bridge } from '@/utils/bridge';
import { priceCalc } from '@/views/spc/spcUtils';
import { getAsinBatch } from '@/models/api/detailPageApi';
import { createPurchase } from '@/models/api/orderConfirmApi';
import constants, { encryptedMarketplaceId } from '@/constants';
import {
  getType,
  getAddressList,
  generateRandomString,
  setCartDataChangeStatus
} from '@/utils/common';
import { clickEvents } from '@/configs/awsRumConfig';
import { awsRumHandler } from '@/utils/awsRumHandler.js';
const {
  DEAL_TYPES,
  FREE_SHIPPING,
  EXTERNAL_PLATFORM,
  EXTERNAL_SELLER_ID,
  AVAILABILITY_STATUS,
  ORDER_REQUEST_ID_LENGTH
} = constants.Common;

const ACTIVE_NAME = {
  ALL: '1',
  DOTD: '2',
  REDUCE_THE_PRICE: '3',
  ALREADY_SOLD_OUT: '4'
};
const CART_TABS_INDEX = {
  ALL: 0,
  DOTD_OR_SPECIAL_SALE_EVENT: 1,
  REDUCE_THE_PRICE: 2,
  ALREADY_SOLD_OUT: 3
};
const addressInfo = ref(null);
// loading status
const isLoading = ref(false);
// whether in pull refresh status
const isRefreshing = ref(false);
// current tab index
const activeTab = ref('1');
// select all
const selectAll = ref(false);
// whether customer in management mode in cart
const isManagement = ref(false);
// is Show DiscountDetails component
const isShowDetail = ref(false);
// Aggregate data
const cartData = ref([]);
// All
const allProductData = ref([]);
// Treasure of the town shop/special sale event
const dotdOrSpecialSaleProductData = ref([]);
// Reduce the price
const reducePriceData = ref([]);
// Already sold out
const outOfStockData = ref([]);
// No longer available
const alreadySoldOutData = ref([]);
// Select all out of stock
const selectAllOutOfStock = ref(false);
// Lump-sum calculation
const calculatedAllPrice = ref({
  totalAmount: 0, // Gross commodity price
  shippingDiscount: 0, // freight
  discount: 0,
  promotionList: []
});
// Free shipping arrangement
const freeShipping = reactive({
  isFreeShipping: false,
  amountFromFSThreshold: 0
});
const selectProductCount = ref(0);

// Is the createPurchase interface reporting an error
const isCreatePurchaseError = ref(false);
// Is the createPurchase interface reporting an success
const isCreatePurchaseSuccessful = ref(null);

/**
 * Due to the presence of button area hollowing out and floating in the middle state of page redirection
 * So it is necessary to use this variable to avoid button area hollowing out and floating
 */
const isHiddenBottomArea = ref(false);

const totalPricesStyle = ref();

const isOutOfStock = cartItem => {
  return cartItem.availability?.status === AVAILABILITY_STATUS.OUT_OF_STOCK;
};

const isSoldOut = cartItem => {
  return !cartItem?.selectionEligible;
};

// Determine out of stock Or off shelf situations
const isOutOfStockOrSoldOut = cartItem => {
  return isOutOfStock(cartItem) || isSoldOut(cartItem);
};

// Calculate the selected quantity
const selectList = computed(() => {
  switch (activeTab.value) {
    case ACTIVE_NAME.ALL:
      if (!isManagement.value) {
        return allProductData.value.filter(
          item => item.check && !isOutOfStockOrSoldOut(item)
        );
      }
      return allProductData.value.filter(item => item.check);
    case ACTIVE_NAME.DOTD:
      return dotdOrSpecialSaleProductData.value.filter(item => item.check);
    case ACTIVE_NAME.REDUCE_THE_PRICE:
      return reducePriceData.value.filter(item => item.check);
    case ACTIVE_NAME.ALREADY_SOLD_OUT:
      return outOfStockData.value
        .concat(alreadySoldOutData.value)
        .filter(item => item.check);
    default:
      return cartData.value.filter(item => item.check);
  }
});

// Get shopping cart data
const getCartData = async callBack => {
  const cartCopyList = [];
  if (!isRefreshing.value) {
    isLoading.value = true;
  }
  Bridge.callFn('getShoppingCartList', {}, async res => {
    const list = res.list;
    if (!list.length) {
      nextTick(() => {
        cartData.value = [];
        isLoading.value = false;
        isRefreshing.value = false;
      });
      return;
    }
    const { list: favoritesList } = await Bridge.callFnVal(
      'getAllFavoriteItemList',
      {}
    )['catch'](() => ({ list: [] }));
    list.forEach(cartItem => {
      cartCopyList.push({
        count: cartItem.count, // Number of items added to cart
        asin: cartItem.productItem.asin, // asin
        merchantId: cartItem.productItem.merchantId,
        ourPrice: cartItem.productItem.ourPrice, // Price
        stocks: cartItem.productItem.stocks, // inventory
        title: cartItem.productItem.title, // title
        mainImageUrl: cartItem.productItem.mainImage, // The main picture of the product display
        availability: cartItem.productItem.availability, // Commodity status
        gsBadge: cartItem.productItem.gsBadge, // Overseas purchase icon
        twisterDisplayString: cartItem.productItem.twisterDisplayString, // Specification copy
        delivery: cartItem.productItem.delivery, // transport
        dealOffer: cartItem.productItem.dealOffer || {}, // Activity type town shop price, etc
        check: cartItem.selected, // Check or not,
        addTime: cartItem.addTime,
        selectionEligible: cartItem.productItem.selectionEligible, // Has the product not been removed from the shelves, true: Not taken off the shelf, false: It's already taken off the shelf
        associateToken: cartItem?.associateToken, // Some information shared, This field is passed to KYC for use
        associateTokenTimeStamp: cartItem?.associateTokenTimeStamp, // Time to add shared items to shopping cart, This field is passed to KYC for use
        isAutoQuota: false, // Whether to restrict purchase
        isFavorite: favoritesList.some(
          avoriteItem =>
            avoriteItem.productItem.asin === cartItem.productItem.asin
        ) // Whether to add favorites
      });
    });
    // Sort by timestamp
    cartCopyList.sort((beforeItem, afterItem) => {
      if (afterItem.addTime > beforeItem.addTime) {
        return 1;
      }
      return -1;
    });

    // Arrange, combine, redefine, sort
    const normalProductArr = [];
    const outOfStockRemovalProductArr = [];
    cartCopyList.forEach(item => {
      if (
        !item.selectionEligible ||
        item.availability?.status === AVAILABILITY_STATUS.OUT_OF_STOCK
      ) {
        outOfStockRemovalProductArr.push(item);
      } else {
        normalProductArr.push(item);
      }
    });
    cartData.value = [...normalProductArr, ...outOfStockRemovalProductArr];
    callBack(isLoading);
  });
};

const updateShoppingCartItem = (asin, data) => {
  Bridge.callFn('updateShoppingCartItem', {
    asin,
    data
  });
};

const updateCartData = async () => {
  const newData = await getAsinBatch({
    ids: cartData.value.map(item => item.asin).toString(),
    marketplace: encryptedMarketplaceId,
    withCache: false,
    allowOtherOffer: false,
    returnableFlagSkipped: false
  })['catch'](() => ({ data: [] }));
  // Gets the price quantity and activity type of the current shopping cart item
  cartData.value.forEach(cartItem => {
    const findData = newData.data?.filter(
      newDataItem => newDataItem.asin === cartItem.asin
    );
    if (findData?.length) {
      const itemAsin = cartItem.asin;
      Object.assign(cartItem, {
        newOurPrice: findData[0].ourPrice?.amount, // The latest price data
        ourPriceObject: findData[0].ourPrice,
        dealOffer: findData[0].dealOffer
      });

      // update dealOffer
      updateShoppingCartItem(itemAsin, {
        productItem: {
          dealOffer: findData[0].dealOffer // Activity type Town shop price
        }
      });

      // update product stocks
      if (cartItem.stocks !== findData[0].stockOnHand) {
        cartItem.stocks = findData[0].stockOnHand;
        updateShoppingCartItem(itemAsin, {
          productItem: {
            stocks: findData[0].stockOnHand
          }
        });
      }

      // update product availability
      if (cartItem.availability?.status !== findData[0].availability?.status) {
        cartItem.availability = findData[0].availability;
        updateShoppingCartItem(itemAsin, {
          productItem: {
            availability: findData[0].availability
          }
        });
      }

      // Modify whether the product has been offTheShelves
      if (cartItem.selectionEligible !== findData[0].selectionEligible) {
        cartItem.selectionEligible = findData[0].selectionEligible; // Has the product not been removed from the shelves, true: Not taken off the shelf, false: It's already taken off the shelf
        updateShoppingCartItem(itemAsin, {
          productItem: {
            selectionEligible: findData[0].selectionEligible
          }
        });
      }

      /*
       *  Determine if the selected quantity exceeds the inventory or if the selected quantity is 0
       *  Update cache data
       */
      if (!cartItem.count || cartItem.count > cartItem.stocks) {
        Object.assign(cartItem, {
          count: cartItem.count ? cartItem.stocks : 1,
          isAutoQuota: true // Whether to restrict purchase
        });
        updateShoppingCartItem(itemAsin, {
          count: cartItem.count ? cartItem.stocks : 1
        });
      }
    }
  });
};

const getAndUpdateData = (callBack = () => {}) => {
  // Retrieve data
  getCartData(async loading => {
    await updateCartData();
    // Wait for the createPurchase interface call to complete before continuing execution
    if (isCreatePurchaseSuccessful.value) {
      await isCreatePurchaseSuccessful.value;
    }
    callBack();
    loading.value = false;
  });
};

// Update quantity of goods
const onUpdateItemCount = ({ asin, count }) => {
  Bridge.callFn('updateShoppingCartCount', { asin, count }, () => {});
  const findIndex = cartData.value.findIndex(item => item.asin === asin);
  cartData.value[findIndex] = Object.assign({}, cartData.value[findIndex], {
    count
  });
  // Mark the shopping cart data change status every time there is a change in the shopping cart data
  setCartDataChangeStatus(true);
};

// Update selected status
const onUpdateItemCheck = ({ asin, value }) => {
  Bridge.callFn(
    'updateShoppingCartItemSelect',
    { asin, select: value },
    () => {}
  );
  cartData.value.forEach(item => {
    if (item.asin === asin) {
      item.check = value;
    }
  });
};

// select all
const onCheckAll = () => {
  cartData.value.forEach(item => {
    if (
      isManagement.value ||
      activeTab.value === ACTIVE_NAME.ALREADY_SOLD_OUT
    ) {
      item.check = selectAll.value;
    } else {
      if (!isOutOfStockOrSoldOut(item)) {
        item.check = selectAll.value;
      }
    }
  });
};

const getOrderDetailList = async () => {
  isLoading.value = true;
  const items = selectList.value.map(item => {
    return {
      asin: item.asin,
      quantity: Number(item.count),
      asinDetail: {
        merchantId: item.merchantId
      }
    };
  });
  isCreatePurchaseSuccessful.value = createPurchase({
    externalPlatform: EXTERNAL_PLATFORM.GS,
    externalSellerId: EXTERNAL_SELLER_ID.DEFAULT,
    items,
    purchaseSessionId: '457-9196418-2840651', // API issue, temporarily using fixed values
    requestId: generateRandomString(ORDER_REQUEST_ID_LENGTH),
    suppressPrice: true,
    addressInfo: {
      addressLine1: addressInfo.value?.address,
      addressLine2: '',
      addressLine3: '',
      addressStr: addressInfo.value?.street + addressInfo.value?.address,
      city: addressInfo.value?.city,
      country: addressInfo.value?.country,
      district: addressInfo.value?.district,
      postalCode: addressInfo.value?.postalCode,
      customerContact: addressInfo.value?.name + addressInfo.value?.phone,
      email: '',
      name: addressInfo.value?.name,
      phoneNumber: addressInfo.value?.phone,
      state: addressInfo.value?.state
    }
  })['catch'](() => {
    isLoading.value = false;
    isCreatePurchaseError.value = true;
    return { data: {} };
  });
  const { data } = await isCreatePurchaseSuccessful.value;
  if (data?.orderDetailList?.length) {
    isCreatePurchaseError.value = false;
    const { allPrice } = priceCalc(data.orderDetailList);
    const { total, shippingDiscount } = allPrice;
    const discount = -(allPrice.discount - shippingDiscount);
    Object.assign(calculatedAllPrice.value, {
      shippingDiscount: (-shippingDiscount).toFixed(2),
      discount: discount.toFixed(2),
      totalAmount: total - discount,
      promotionList: Array.from(allPrice.promotionList.values())
    });
    isLoading.value = false;
  }
};

const getAddressListData = async () => {
  const { addressInfo: addrInfo } = await getAddressList();
  addressInfo.value = addrInfo;
};

// Calculate the total count of product
const computeProductsQuantity = productList => {
  let count = 0;
  count = productList?.length
    ? productList.reduce((previousValue, currentValue) => {
        if (getType(previousValue) === 'number') {
          return previousValue + currentValue.count;
        }
        return previousValue.count + currentValue.count;
      })
    : 0;
  return count;
};

// Price calculation method
const calculatePrice = () => {
  let total = 0;
  let amountFromFSThreshold = 0;
  selectList.value?.forEach(item => {
    const price = item.newOurPrice ? item.newOurPrice : item.ourPrice;
    total += price * item.count;
    if (item.delivery?.isGlobalStoreFreeShippingProgram) {
      amountFromFSThreshold += price * item.count;
    }
  });
  freeShipping.amountFromFSThreshold = amountFromFSThreshold.toFixed(2);
  return total;
};

const handleOrderFreeShipping = () => {
  const total = calculatePrice();
  if (
    freeShipping.amountFromFSThreshold >=
      FREE_SHIPPING.FREE_SHIPPING_THRESHOLD &&
    !isManagement.value
  ) {
    freeShipping.isFreeShipping = true;
    if (!addressInfo.value) {
      calculatedAllPrice.value.totalAmount = total.toFixed(2);
      return;
    }
    getOrderDetailList();
  } else {
    calculatedAllPrice.value.totalAmount = total.toFixed(2);
    freeShipping.isFreeShipping = false;
  }
};

// Select  all items that are out of stock
const onClickAllOutOfStock = () => {
  outOfStockData.value.forEach(item => {
    if (item.availability.status === AVAILABILITY_STATUS.OUT_OF_STOCK) {
      item.check = selectAllOutOfStock.value;
    }
  });
};

watch(
  selectList,
  newList => {
    selectProductCount.value = 0;
    newList.forEach(item => {
      selectProductCount.value += item.count ? item.count : 1;
    });
  },
  { deep: true }
);

watch(isManagement, () => {
  if (!isManagement.value) {
    cartData.value.forEach(item => {
      if (isOutOfStockOrSoldOut(item)) {
        item.check = false;
      }
    });
    // Every time I switch back to the management mode, I calculate the price change
    handleOrderFreeShipping();
  }
  awsRumHandler.recordClickEvent(clickEvents.cartPage.manageCart_btn);
});

/*
 * todo This method needs to be optimized to reduce the number of calls. Currently, we cannot figure out how to optimize it before further research
 * The calculation of price depends on the change of address, so the price needs to be recalculated after the selected product changes or the address changes
 */
watch(
  [selectList, addressInfo, isRefreshing],
  (newList, oldList) => {
    /**
     * If the dropdown loading has just ended
     * Calculate prices from scratch
     */
    if (newList[2] === false && oldList[2] === true && !isManagement.value) {
      handleOrderFreeShipping();
    }
    /**
     * If the product selection has not changed
     * And the count of the product has not changed
     * And the address has not changed
     * Or management mode
     * Do not proceed with subsequent price calculation procedures if all conditions are met
     * */
    if (
      (newList[0]?.length === oldList[0]?.length &&
        computeProductsQuantity(newList[0]) ===
          computeProductsQuantity(oldList[0]) &&
        newList[1]?.adcode === oldList[1]?.adcode) ||
      isManagement.value ||
      isRefreshing.value
    ) {
      return;
    }
    handleOrderFreeShipping();
  },
  { deep: true }
);

watch(
  cartData,
  newCartData => {
    // Total data
    allProductData.value = newCartData;
    // Town shop treasure/special sale event
    dotdOrSpecialSaleProductData.value = newCartData.filter(item => {
      return (
        (item.dealOffer?.dealType === DEAL_TYPES.DEAL_OF_THE_DAY ||
          item.dealOffer?.dealType === DEAL_TYPES.BEST_DEAL) &&
        !isOutOfStockOrSoldOut(item)
      );
    });
    // Reduce the price
    reducePriceData.value = newCartData.filter(
      item => item.newOurPrice < item.ourPrice
    );
    // Out of stock
    outOfStockData.value = newCartData.filter(
      item =>
        item.availability?.status === AVAILABILITY_STATUS.OUT_OF_STOCK &&
        item.selectionEligible
    );
    // No longer available
    alreadySoldOutData.value = newCartData.filter(
      item => !item.selectionEligible
    );
    if (isManagement.value) {
      if (newCartData.every(item => item.check)) {
        selectAll.value = true;
      } else {
        selectAll.value = false;
      }
    } else {
      if (
        newCartData
          .filter(item => !isOutOfStockOrSoldOut(item))
          .every(item => item.check)
      ) {
        selectAll.value = true;
      } else {
        selectAll.value = false;
      }
    }

    if (outOfStockData.value.every(item => item.check)) {
      selectAllOutOfStock.value = true;
    } else {
      selectAllOutOfStock.value = false;
    }
  },
  { deep: true }
);

export default {
  cartState: {
    cartData,
    selectAll,
    isLoading,
    activeTab,
    selectList,
    ACTIVE_NAME,
    addressInfo,
    freeShipping,
    isManagement,
    isShowDetail,
    isRefreshing,
    outOfStockData,
    allProductData,
    CART_TABS_INDEX,
    reducePriceData,
    totalPricesStyle,
    alreadySoldOutData,
    isHiddenBottomArea,
    calculatedAllPrice,
    selectProductCount,
    selectAllOutOfStock,
    isCreatePurchaseError,
    isCreatePurchaseSuccessful,
    dotdOrSpecialSaleProductData
  },
  handleCartState: {
    onCheckAll,
    getCartData,
    getAndUpdateData,
    onUpdateItemCount,
    onUpdateItemCheck,
    getAddressListData,
    getOrderDetailList,
    onClickAllOutOfStock,
    isOutOfStockOrSoldOut
  }
};
